import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import adminOperationsService from '../../services/adminOperationsService';

const sections = [
  {
    label: 'Command center',
    items: [
      { name: 'Dashboard', href: '/admin', icon: '⌁' },
      { name: 'System health', href: '/admin/system', icon: '◉' },
    ],
  },
  {
    label: 'Exchange operations',
    items: [
      { name: 'Assets & pairs', href: '/admin/coins-pairs', icon: '◇' },
      { name: 'Orders', href: '/admin/orders', icon: '↕' },
      { name: 'Trading', href: '/admin/trading', icon: '⌁' },
      { name: 'Wallet ledger', href: '/admin/wallet-ledger', icon: '▤' },
      { name: 'Deposits', href: '/admin/deposits', icon: '↓' },
      { name: 'Withdrawals', href: '/admin/withdrawals', icon: '↑' },
    ],
  },
  {
    label: 'Customers & treasury',
    items: [
      { name: 'Users', href: '/admin/users', icon: '◎' },
      { name: 'Keeper wallets', href: '/admin/keeper', icon: '▣' },
      { name: 'Quick swap', href: '/admin/quick-swap', icon: '⇄' },
      { name: 'Marketplace', href: '/admin/marketplace-orders', icon: '□' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { name: 'Fees & limits', href: '/admin/settings', icon: '⚙' },
      { name: 'Bots', href: '/admin/bots', icon: '◌' },
      { name: 'Notifications', href: '/admin/notifications', icon: '✦' },
      { name: 'All admin resources', href: '/admin/resources', icon: '▦' },
    ],
  },
];

const isCurrent = (pathname, href) => href === '/admin' ? pathname === href : pathname.startsWith(href);

const Sidebar = ({ location, onNavigate }) => (
  <div className="flex h-full flex-col bg-slate-950">
    <div className="border-b border-slate-800 px-5 py-5">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Operations console</p>
      <h1 className="mt-1 text-xl font-bold text-white">Exchange Admin</h1>
    </div>
    <nav className="flex-1 overflow-y-auto px-3 py-4">
      {sections.map((section) => (
        <div className="mb-5" key={section.label}>
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{section.label}</p>
          <div className="space-y-1">
            {section.items.map((item) => (
              <Link key={item.href} to={item.href} onClick={onNavigate}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${isCurrent(location.pathname, item.href)
                  ? 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/20'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100'}`}>
                <span className="w-5 text-center text-base" aria-hidden="true">{item.icon}</span>
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </nav>
    <div className="border-t border-slate-800 p-4 text-xs text-slate-500">Production controls · audited access</div>
  </div>
);

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = adminOperationsService.getStoredUser();
  const allItems = useMemo(() => sections.flatMap((section) => section.items), []);
  const matches = search.trim()
    ? allItems.filter((item) => item.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 6)
    : [];

  const logout = () => {
    adminOperationsService.logout();
    window.dispatchEvent(new Event('admin-session-expired'));
    navigate('/admin', { replace: true });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    if (matches[0]) {
      navigate(matches[0].href);
      setSearch('');
    }
  };

  return (
    <div className="admin-console min-h-screen bg-slate-900 text-slate-100">
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <button aria-label="Close navigation" className="absolute inset-0 bg-black/70" onClick={() => setSidebarOpen(false)} />
          <aside className="relative h-full w-72 border-r border-slate-800 shadow-2xl">
            <Sidebar location={location} onNavigate={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-slate-800 lg:block">
        <Sidebar location={location} />
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-slate-800 bg-slate-900/95 px-4 backdrop-blur sm:px-6">
          <button aria-label="Open navigation" className="rounded-lg border border-slate-700 p-2 text-slate-300 lg:hidden" onClick={() => setSidebarOpen(true)}>☰</button>
          <form className="relative min-w-0 flex-1" onSubmit={submitSearch}>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Jump to users, wallets, pairs, settings…"
              className="w-full max-w-xl rounded-lg border border-slate-700 bg-slate-950 px-4 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-cyan-500" />
            {matches.length > 0 && (
              <div className="absolute left-0 top-11 w-full max-w-xl overflow-hidden rounded-lg border border-slate-700 bg-slate-950 shadow-2xl">
                {matches.map((item) => (
                  <button type="button" key={item.href} onClick={() => { navigate(item.href); setSearch(''); }}
                    className="flex w-full items-center gap-3 border-b border-slate-800 px-4 py-3 text-left text-sm text-slate-300 last:border-0 hover:bg-slate-800">
                    <span>{item.icon}</span>{item.name}
                  </button>
                ))}
              </div>
            )}
          </form>
          <div className="hidden text-right sm:block">
            <p className="text-sm font-medium text-slate-200">{adminUser?.email || adminUser?.username || 'Administrator'}</p>
            <p className="text-xs text-emerald-400">Staff session active</p>
          </div>
          <button onClick={logout} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-red-500/60 hover:text-red-300">Sign out</button>
        </header>

        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
